<template>
  <div class="app-wrapper">
    <div class="dev-docs-app">
      <Suspense>
        <div class="h-full">
          <loader v-if="isApiFetchingData" />
          <not-found-error-page v-else-if="apiNotFoundError" />
          <error-page v-else-if="apiUnexpectedError" />
          <router-view :class="{ hidden: isMainContentHidden }" />
        </div>

        <!-- loading state -->
        <template #fallback>
          <loader />
        </template>
      </Suspense>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, toRefs, watch, watchEffect } from 'vue';
import { RouteLocationNormalized, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash';
import Loader from '@/components/loader/Loader.vue';
import { ApiDataFetchingState, useApiStore } from '@/store/api';
import ErrorPage from '@/pages/system/ErrorPage.vue';
import NotFoundErrorPage from '@/pages/system/NotFoundErrorPage.vue';
import { getExternalUrlFromPath } from '@/utils/url';
import { setDateFormatLocale } from '@/utils/date';
import { scrollToElementHash } from '@/utils/scrolling';
import { documentImagesHaveRendered } from '@/utils/document';
import { setApplicationMetaTags, setPageTagsForRoute } from '@/metadata';
import useUserStore from '@/store/user';
import { isAppMounted } from '@/utils/singleSpa';

const location = useRoute();
const props = defineProps({
  userStore: { type: Object, required: true },
  applyRouteSideEffects: { type: Boolean, required: true },
});

watchEffect(() => {
  if (!props.userStore) {
    return;
  }
  const userStore = useUserStore();
  userStore.info = props.userStore.info;
});

const { apiDateFetchingState } = toRefs(useApiStore());
const isApiFetchingData = computed(() => apiDateFetchingState.value === ApiDataFetchingState.Fetching);
const apiNotFoundError = computed(() => apiDateFetchingState.value === ApiDataFetchingState.ContentNotFound);
const apiUnexpectedError = computed(() => apiDateFetchingState.value === ApiDataFetchingState.UnexpectedError);
const isMainContentHidden = computed(
  () => isApiFetchingData.value || apiNotFoundError.value || apiUnexpectedError.value
);

const router = useRouter();
router.beforeEach(async (to, from, next) => {
  if (!isAppMounted() || to.fullPath === from.fullPath) {
    // Ignore navigation to the same route
    /**
     * To avoid Vue Warning related to invalid router navigation
     * */
    next();
    return;
  }

  // handle absolute urls as this is not provided by
  // default by the vue router
  const absoluteUrl = getExternalUrlFromPath(to.fullPath);
  if (absoluteUrl) {
    window.location.href = absoluteUrl;
    return;
  }

  const { setApiDateFetchingState } = useApiStore();

  // reset API loading states on route change
  setApiDateFetchingState(ApiDataFetchingState.None);

  next();
});

// set current locale for date formats
const i18n = useI18n();
onBeforeMount(() => {
  setDateFormatLocale(i18n.locale.value);
});

// set application wide meta tags
onMounted(setApplicationMetaTags);

// need to debounce this due to a bug in single SPA & Vue Router 4
// https://github.com/single-spa/single-spa/issues/929
const applyRouteSideEffectsFn = debounce(async (to: RouteLocationNormalized) => {
  const shouldTrackPageView = to.meta?.analytics?.trackPageView ?? true;
  if (shouldTrackPageView) {
    window.analytics.onPageLoad();
  }
  await setPageTagsForRoute(i18n, to);

  // use next tick to wait until the route component has finished rendering
  // to tell prerender we are done loading the content
  nextTick(() => {
    window.prerender?.pageHasFinishedRendering();
  });
}, 200);

// set page specific meta tags
router.afterEach(async to => {
  if (!isAppMounted()) return;

  if (props.applyRouteSideEffects) {
    applyRouteSideEffectsFn(to);
  }
});

router.onError(error => {
  // eslint-disable-next-line no-console
  console.error('Error while loading route', error);

  /**
   * When a new version of the application has been deployed, we might run into 404 errors when loading chunks (dynamic
   * imports) from the old version. In this case, we reload the page to get the new version.
   * The error is: "TypeError: error loading dynamically imported module"
   */
  if (error.message.includes('error loading dynamically imported module')) {
    window.location.reload();
  }
});

const { hash } = toRefs(useRoute());
watch(
  () => apiDateFetchingState.value,
  async (currentState, oldValue) => {
    if (oldValue === ApiDataFetchingState.Fetching && currentState === ApiDataFetchingState.Fetched) {
      // scroll to correct anchor if hash is provided
      if (hash.value) {
        setTimeout(async () => {
          // wait until all images have rendered so that all elements position are finalized
          await documentImagesHaveRendered();

          scrollToElementHash(hash.value);
        });
      }
    }
  }
);

watch(
  () => location.fullPath,
  () => {
    // location.fullPath will be the location of the current route without the base e.g. "/use-cases"
    // Use setTimeout to ensure this code runs after the code in portal with will set state.current including the base
    window.setTimeout(() => {
      window.history.state.current = location.fullPath;
    }, 0);
  }
);
</script>

<style lang="scss">
@import './styles/index';
.single-spa-container {
  height: 100% !important;
}
.app-wrapper {
  height: 100%;
}
.dev-docs-app {
  strong {
    font-size: unset !important;
  }
}
</style>
